/**
 * @engine/core - Time
 * 
 * Time management for game loop.
 * Provides delta time, fixed time, and time scale.
 */

/**
 * Time state
 */
export interface TimeState {
  /** Total time since start (seconds) */
  time: number;
  /** Time since last frame (seconds) */
  deltaTime: number;
  /** Unscaled delta time */
  unscaledDeltaTime: number;
  /** Fixed delta time for physics */
  fixedDeltaTime: number;
  /** Total frames rendered */
  frameCount: number;
  /** Time scale (1.0 = normal) */
  timeScale: number;
}

/**
 * Time - Global time management
 */
export class Time {
  private _time = 0;
  private _deltaTime = 0;
  private _unscaledDeltaTime = 0;
  private _fixedDeltaTime = 1 / 60; // 60 FPS physics
  private _frameCount = 0;
  private _timeScale = 1.0;
  
  private _lastTimestamp = 0;
  private _fixedAccumulator = 0;
  
  /** Total time since start */
  get time(): number {
    return this._time;
  }
  
  /** Time since last frame (scaled) */
  get deltaTime(): number {
    return this._deltaTime;
  }
  
  /** Time since last frame (unscaled) */
  get unscaledDeltaTime(): number {
    return this._unscaledDeltaTime;
  }
  
  /** Fixed delta time for physics */
  get fixedDeltaTime(): number {
    return this._fixedDeltaTime;
  }
  
  set fixedDeltaTime(value: number) {
    this._fixedDeltaTime = Math.max(0.001, value);
  }
  
  /** Total frames rendered */
  get frameCount(): number {
    return this._frameCount;
  }
  
  /** Time scale (0 = paused, 1 = normal, 2 = double speed) */
  get timeScale(): number {
    return this._timeScale;
  }
  
  set timeScale(value: number) {
    this._timeScale = Math.max(0, value);
  }
  
  /**
   * Initialize time (call at start)
   */
  init(timestamp: number = 0): void {
    this._lastTimestamp = timestamp;
    this._time = 0;
    this._frameCount = 0;
    this._fixedAccumulator = 0;
  }
  
  /**
   * Update time (call each frame)
   * Returns number of fixed updates needed
   */
  update(timestamp: number): number {
    // Calculate delta
    const dt = Math.min((timestamp - this._lastTimestamp) / 1000, 0.25); // Cap at 250ms
    this._lastTimestamp = timestamp;
    
    this._unscaledDeltaTime = dt;
    this._deltaTime = dt * this._timeScale;
    this._time += this._deltaTime;
    this._frameCount++;
    
    // Calculate fixed updates
    this._fixedAccumulator += this._deltaTime;
    let fixedUpdates = 0;
    
    while (this._fixedAccumulator >= this._fixedDeltaTime) {
      this._fixedAccumulator -= this._fixedDeltaTime;
      fixedUpdates++;
      
      // Safety: max 8 fixed updates per frame
      if (fixedUpdates >= 8) {
        this._fixedAccumulator = 0;
        break;
      }
    }
    
    return fixedUpdates;
  }
  
  /**
   * Get state for serialization/debugging
   */
  getState(): TimeState {
    return {
      time: this._time,
      deltaTime: this._deltaTime,
      unscaledDeltaTime: this._unscaledDeltaTime,
      fixedDeltaTime: this._fixedDeltaTime,
      frameCount: this._frameCount,
      timeScale: this._timeScale,
    };
  }
}
